import { Component, inject, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { SingleActionDialogModel } from '../../../interfaces/dialogs/single-action-dialog.model';
import { MatButton } from '@angular/material/button';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { companyNoValidator } from '../../../utils/validator.util';
import { CompanyCodeOnlyDirective } from '../../../../directives/company-code-only.directive';
import { VerifyEnterpriseService } from '../../../services/verify-enterprise.service';
import { SharedStorageService } from '../../../services/shared-storage.service';
import { Router } from '@angular/router';

/**
 * 單一按鈕通用dialog組件，對應樣式A
 */
@Component({
  selector: 'app-input-companyNo-dialog',
  standalone: true,
  imports: [MatDialogContent, MatButton, TranslatePipe, ReactiveFormsModule, CompanyCodeOnlyDirective],
  templateUrl: './input-companyNo-dialog.html',
  styleUrl: './input-companyNo-dialog.scss',
})
export class InputCompanyNoDialogComponent {
  translateService: TranslateService = inject(TranslateService);
  verifyEnterpriseService: VerifyEnterpriseService = inject(VerifyEnterpriseService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  router: Router = inject(Router);
  
  enterpriseCodeControl = new FormControl('', [
    Validators.required,
    (control: FormControl) => companyNoValidator(control, this.translateService)
  ]);

  isFormSubmitted = false;
  isButtonDisabled = false;

  constructor(
    public dialogRef: MatDialogRef<InputCompanyNoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SingleActionDialogModel
  ) {}

  ngOnInit() {
    // 監聽企業代碼輸入變化，清除錯誤狀態
    this.enterpriseCodeControl.valueChanges.subscribe(() => {
      if (this.hasError) {
        this.isFormSubmitted = false;
      }
    });
  }

  // 計算確認按鈕是否應該被禁用
  get isConfirmDisabled(): boolean {
    return this.isButtonDisabled || (this.isFormSubmitted && this.enterpriseCodeControl.invalid) || !this.enterpriseCodeControl.value?.trim();
  }

  // 是否顯示錯誤訊息
  get hasError(): boolean {
    return this.enterpriseCodeControl.invalid && this.isFormSubmitted;
  }

  close() {
    this.dialogRef.close();
  }

  // 確認按鈕
  onConfirm() {
    // 預防多次點擊
    if (this.isButtonDisabled) {
      return;
    }
    this.isFormSubmitted = true;
    
    if (this.enterpriseCodeControl.valid && this.enterpriseCodeControl.value?.trim() !== '') {
      this.isButtonDisabled = true;
      this.enterpriseCodeControl.markAllAsTouched();

      // call CB-040 查詢企業代碼狀態
      this.verifyEnterpriseService.verifyEnterpriseInfo(this.enterpriseCodeControl.value?.trim() ?? '').subscribe({
        next: (res) => {
          if (res.statusCode === 'S0000') {
            // 是否超過企業可預約時間
            if (res.data?.isOverBookableDate === true) {
              this.enterpriseCodeControl.setErrors({ overDate: true });
            } else {
              // 儲存企業代碼並關閉對話框
              this.sharedStorageService.set('controlItem', res.data?.controlItem ?? 0);
              this.sharedStorageService.set('companyNo', this.enterpriseCodeControl.value?.trim() ?? '');
              this.dialogRef.close(this.enterpriseCodeControl.value?.trim());
              this.router.navigate(['/healthcheck/companylogin']).then();
            }
          } else if (res.statusCode === 'EL1007') {
            this.enterpriseCodeControl.setErrors({ notFound: true });
          } else if (res.statusCode === 'CRM_9144') {
            this.enterpriseCodeControl.setErrors({ notFound: true });
          } 
          // 暫定都先回傳查無企業代碼
          else {
            this.enterpriseCodeControl.setErrors({ notFound: true });
          }
          this.isButtonDisabled = false;
        },
        error: (err) => {
          this.isButtonDisabled = false;
          this.enterpriseCodeControl.setErrors({ notFound: true });
        }
      });
      return;
    }
  }

  // 當用戶開始輸入時清除錯誤
  onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const sanitizedValue = input.value.replace(/[^0-9-]/g, '').slice(0, 10);
    
    // 同步更新 FormControl 的值
    if (this.enterpriseCodeControl.value !== sanitizedValue) {
      this.enterpriseCodeControl.setValue(sanitizedValue, { emitEvent: false });
    }
    
    // 清除錯誤狀態
    if (this.hasError) {
      this.isFormSubmitted = false;
    }
    
    // 重新驗證
    this.enterpriseCodeControl.updateValueAndValidity();
  }

  // 獲取企業代碼錯誤訊息
  getCompanyCodeErrorMessage(): string {
    const control = this.enterpriseCodeControl;
    if (!control || !control.errors) return '';

    const errors = control.errors;
    
    if (errors['required']) {
      return this.translateService.instant('error.company-code-error.required');
    }
    if (errors['overDate']) {
      return this.translateService.instant('error.company-code-error.overDate');
    }
    if (errors['notFound']) {
      return this.translateService.instant('error.company-code-error.not-found');
    }
    
    return '';
  }
}
